/**
 * Aktions-Schlüssel, Definitionen, Executor- und Registry-Schnittstellen ([V] 3.1, 4.3 `actions.ts`;
 * [MCP] 9.3, 20.2; Plan Welle 0 Teil A Anhang A.actions).
 *
 * - `ACTION_KEYS` ist eine Konstante in Katalogreihenfolge (69 Schlüssel); die Daten der Tabelle 3.1
 *   liegen in `action-catalog.ts`, der Test `action-catalog.test.ts` gleicht beide mit dem
 *   Vertragsdokument ab.
 * - `RESERVED_ACTION_KEYS`: Katalogeinträge, deren zulässige Modi nur `off` sind. Die Richtlinienzeile
 *   existiert, ein `OperationExecutor` wird nicht registriert (`ActionRegistry.register` lehnt ab).
 * - `ActionDayCounter` ([V] E-j Nr. 2b, v9): Tageszählung je `action_key` (Kalendertag Europe/Berlin).
 * - Nachtrag C-2 (Plan Welle 1 Teil C, 0.4a): ausgehende Executoren tragen `manualOutbound` + `handoff`
 *   (`manual_outbound`) bzw. `apiPublish` (`api_outbound`); `ActionRegistry.register` lehnt fehlende Teile ab.
 *
 * Importzyklus `actions ↔ approval ↔ locks ↔ operations` nur über `import type` (zur Laufzeit zyklenfrei).
 */
import type { ApprovalMode } from './approval.js';
import type { DbTx, DomainContext, DomainResult, PersonId } from './common.js';
import type { LedgerHolder, OutboundCheck } from './compliance.js';
import type { PhraseContext } from './content.js';
import type { HeldLocks } from './locks.js';
import type { OperationRef, OperationStatus } from './operations.js';

export const ACTION_KEYS = [
  'identity.decision',
  'relationship.strength_feedback',
  'person.merge',
  'person.restrict',
  'person.erase',
  'account.refresh',
  'task.complete',
  'task.snooze',
  'task.dismiss',
  'task.more',
  'task.create',
  'audience.upsert',
  'audience.member_override',
  'goal.set',
  'sequence.upsert',
  'sequence.activate',
  'sequence.enroll',
  'sequence.enrollment.stop',
  'meeting.record',
  'consent.grant.record',
  'consent.optout.record',
  'consent.block.lift',
  'privacy.art14.record',
  'target.set',
  'target.research.run',
  'target.research.refresh',
  'target.candidate.decide',
  'target.erase',
  'warmup.plan.start',
  'warmup.plan.revise',
  'warmup.step.emit_task',
  'warmup.step.complete_detected',
  'warmup.step.update',
  'warmup.draft.generate',
  'radar.post_add',
  'radar.adopt',
  'm365.mail_meta.sync',
  'm365.mail_radar.sync',
  'odoo.read.sync',
  'comm.intent.classify',
  'comm.draft.auto_generate',
  'comm.draft.auto_generate_followup',
  'comm.followup.create_from_intent',
  'comm.followup.create_after_send',
  'comm.followup.close_on_inbound',
  'followup.set',
  'comm.meeting.auto_propose',
  'comm.conversation.auto_spam',
  'calendar.event.create',
  'linkedin.message.send',
  'linkedin.invitation.send',
  'linkedin.comment.create',
  'linkedin.reaction.create',
  'linkedin.post.manual',
  'linkedin.post.publish',
  'post.draft.save',
  'visibility.voice.profile.activate',
  'visibility.voice.samples.messages',
  'visibility.ai.freetext_send',
  'visibility.ai.excerpt_send',
  'visibility.topic.accept',
  'visibility.calendar.apply_suggestion',
  'visibility.topic.create',
  'visibility.practice_note.add',
  'visibility.content.answer',
  'visibility.comment.draft',
  'prospect.search.draft',
  'prospect.assess.run',
  'prospect.web.run',
] as const;
export type ActionKey = (typeof ACTION_KEYS)[number];

/** Registrierungsdaten je Schlüssel (Werte aus Tabelle 3.1 plus Texte für die Einstellungsseite). */
export interface ActionDefinition {
  key: ActionKey;
  label: string;
  explanation: string;
  category: 'outbound' | 'internal';
  reversible: boolean;
  executionMode: 'internal' | 'manual_outbound' | 'api_outbound';
  allowedModes: ApprovalMode[];
  defaultMode: ApprovalMode;
  thirdPartyTrigger: 'allowed' | 'configurable' | 'never';
  registryDailyCap: number | null;
  gateMode: 'shared' | 'exclusive';
  scoreSource: 'relationship_strength' | 'audience_score' | null;
  supportedConditions: string[];
}

/** Eingabe der Übergabe an den Menschen (C-2): Halter der Budgetbuchung und optionale Bestätigung. */
export type HandoffInput = { holder: LedgerHolder; confirmations?: { not_promotional?: true } };

/** Ergebnis der Übergabe (C-2; [MCP] 20.2): `deepLink` ist im Demo-Modus `null`. */
export type HandoffOutput = { text: string; deepLink: string | null; art14Appended: boolean };

/** Pflichtteil eines Executors mit `executionMode` `manual_outbound` (C-2): Prüfung, Qualitätstext, Bearbeitungen. */
export interface ManualOutboundSpec<P> {
  outboundCheck(
    tx: DbTx,
    ctx: DomainContext,
    payload: P,
    a: { operationId: string },
  ): Promise<OutboundCheck>;
  /** `null` nur bei Reaktionen (kein Text), siehe `QUALITY_TEXT_NULLABLE_ACTION_KEYS`. */
  qualityText(payload: P): { text: string; lang: 'de' | 'en'; context: PhraseContext } | null;
  applyEdits(payload: P, edits: { text?: string }): P;
}

/** Pflichtteil eines Executors mit `executionMode` `api_outbound` (`linkedin.post.publish`, C-2). */
export interface ApiPublishSpec<P> {
  /** `actionIdentity` = `post:<contentItemId>`. */
  publishCheck(
    tx: DbTx,
    ctx: DomainContext,
    payload: P,
    a: { operationId: string },
  ): Promise<{ contentItemId: string; connectedAccountId: string; actionIdentity: string }>;
  /** nie `null`: Beiträge haben immer einen Text. */
  qualityText(payload: P): { text: string; lang: 'de' | 'en'; context: PhraseContext };
  applyEdits(payload: P, edits: { text?: string }): P;
}

/**
 * Schlüssel, für die `ManualOutboundSpec.qualityText` `null` liefern darf (Reaktionen tragen keinen Text).
 * Für jeden anderen Schlüssel lehnt die Engine `null` fail-closed ab (`QUALITY_CHECK_FAILED` für
 * `linkedin.post.*`, sonst Programmierfehler).
 */
export const QUALITY_TEXT_NULLABLE_ACTION_KEYS = [
  'linkedin.reaction.create',
] as const satisfies readonly ActionKey[];

/**
 * Rollen eines Personenbezugs (`mcp_operation_person_ref.role`, [MCP] 7.10 mit CHECK `ck_mcp_operation_person_ref_role`).
 * Die Liste ist die des Schemas (`PERSON_REF_ROLE_VALUES` in `@socialfy/db`; die Gleichheit prüft der Test in `core`):
 * `target` Gegenstand der Handlung (Zielperson, Einwilligung, Termin, Zielgruppe), `recipient` Empfänger einer Nachricht oder
 * Einladung (auch einer Ablauf-Aufnahme), `conversation_participant` Gesprächspartner, `post_author` Autor des Beitrags, auf
 * den sich Kommentar oder Reaktion beziehen, `task_subject` Person einer Aufgabe, `mentioned` Person, deren Daten nur im
 * Inhalt vorkommen. Die Löschhaken (`onPersonErased`, `onPersonRestricted`, `onPersonsMerged`, `onPersonBlocked`) lesen die
 * Bezüge **ohne** Rolle (nur `person_id`), die Rolle dient der Anzeige und der Prüfung der Datenbank.
 */
export const PERSON_REF_ROLES = [
  'target',
  'recipient',
  'conversation_participant',
  'post_author',
  'task_subject',
  'mentioned',
] as const;
export type PersonRefRole = (typeof PERSON_REF_ROLES)[number];

/** Ein Personenbezug eines Vorgangs, wie `OperationExecutor.personRefs` ihn liefert. */
export type PersonRef = { personId: string; role: PersonRefRole };

/** Ein Executor je `action_key` ([V] E-l Nr. 2; [MCP] 20.2). `planLocks` ist rein lesend. */
export interface OperationExecutor<P> {
  definition: ActionDefinition;
  validate(tx: DbTx, ctx: DomainContext, payload: P): Promise<DomainResult<void>>;
  targetKey(payload: P): string;
  planLocks(
    tx: DbTx,
    ctx: DomainContext,
    payload: P,
  ): Promise<{
    domainKeys: string[];
    /** `block` ergänzt Kontaktsperren als Lebenszyklusart (E-n Nr. 1, R-8). */
    lifecycle?: { kind: 'merge' | 'restrict' | 'erase' | 'block'; personIds: string[] };
  }>;
  personRefs(tx: DbTx, ctx: DomainContext, payload: P): Promise<PersonRef[]>;
  fingerprint(tx: DbTx, ctx: DomainContext, payload: P): Promise<Uint8Array>;
  actionFingerprint?(finalPayload: P): Uint8Array;
  preview(tx: DbTx, ctx: DomainContext, payload: P): Promise<unknown>;
  apply?(
    tx: DbTx,
    ctx: DomainContext,
    held: HeldLocks,
    finalPayload: P,
  ): Promise<DomainResult<Record<string, unknown>>>;
  /** Pflicht für `manual_outbound` (zusammen mit `manualOutbound`, C-2). */
  manualOutbound?: ManualOutboundSpec<P>;
  /** Pflicht für `api_outbound` (C-2). */
  apiPublish?: ApiPublishSpec<P>;
  handoff?(
    tx: DbTx,
    ctx: DomainContext,
    held: HeldLocks,
    finalPayload: P,
    input: HandoffInput,
  ): Promise<DomainResult<HandoffOutput>>;
  score?(tx: DbTx, ctx: DomainContext, payload: P): Promise<number | null>;
}

export interface ActionRegistry {
  /**
   * Wirft bei reservierten Schlüsseln (`RESERVED_ACTION_KEYS`), Doppelregistrierung und Regelverstößen,
   * darunter (C-2) `manual_outbound` ohne `manualOutbound` + `handoff` und `api_outbound` ohne `apiPublish`.
   */
  register<P>(executor: OperationExecutor<P>): void;
  get(key: ActionKey): OperationExecutor<unknown>;
  list(): ActionDefinition[];
}

/**
 * Fachlicher Anteil je Herkunft einer ausgehenden LinkedIn-Aktion ([V] E-l Nr. 2).
 *
 * Nachtrag C-W2 (Pläne Welle 2 A/B):
 * - `planLocks` liefert **mindestens** `person:<id>` (Obermenge); weitere Schlüssel der Variante (`target:`,
 *   `warmup_step:`, `radar:`, `conversation:`, `reply_draft:`) sind zulässig. Die Engine nimmt sie in ihren Sperrplan
 *   auf; jede Wirkung (`onApproved`, `onHandedOff`, `onTransition`) prüft **alle** geplanten Schlüssel mit
 *   `held.assertHolds` und fordert nie nach.
 * - `onHandedOff` (C-T3) erhält den finalen Übergabetext (`HandoffOutput.text` inkl. Art.-14-Anhang, `deepLink` im
 *   Demo-Modus `null`, `art14Appended`); die Engine ruft den Hook nur bei der **ersten** Übergabe, in der
 *   Übergabetransaktion vor der Ausgabe. Ein Verstoß dort (`DomainFailure`) rollt die Übergabe zurück, kein Text.
 * - Nutzlastformen (Vorschlag der Pläne, RW-10): `warmup`/`warmup_comment` `{ personId, stepId }`, `radar_comment`
 *   `{ personId: PersonId | null, radarItemId }`, `reply` `{ personId, conversationId, … }` (Plan 2-B `ReplyVariantPayload`).
 *   Der generische `linkedin.*`-Executor ruft `validate` bei **jeder** Übergabe in der Übergabetransaktion vor der
 *   Ausgabe (auch bei Wiederholungen, für die `onHandedOff` nicht mehr läuft).
 */
export interface OutboundVariantHandler<P = unknown> {
  variant: 'reply' | 'new' | 'warmup' | 'sequence' | 'radar_comment' | 'warmup_comment';
  actionKey: Extract<ActionKey, `linkedin.${string}`>;
  validate(tx: DbTx, ctx: DomainContext, payload: P): Promise<DomainResult<void>>;
  planLocks(tx: DbTx, ctx: DomainContext, payload: P): Promise<string[]>;
  onApproved(tx: DbTx, held: HeldLocks, op: OperationRef): Promise<void>;
  onHandedOff(tx: DbTx, held: HeldLocks, op: OperationRef, out: HandoffOutput): Promise<void>;
  onTransition(tx: DbTx, held: HeldLocks, op: OperationRef, status: OperationStatus): Promise<void>;
}

/** Katalogschlüssel ohne OperationExecutor in V1: genau die Einträge, deren zulässige Modi nur `off` sind. */
export const RESERVED_ACTION_KEYS = [
  'person.erase',
  'target.erase',
  'calendar.event.create',
] as const satisfies readonly ActionKey[];

/**
 * Tageszählung je action_key (Kalendertag Europe/Berlin) für daily_limit, max_auto_per_day und
 * registryDailyCap ([V] E-j Nr. 2b). Eigentum `packages/operations`; Tabellen
 * `action_day_count`/`action_day_execution` (mcp.ts).
 */
export type ActionExecutionMode = 'auto' | 'human'; // auto = MCP/Automatik ohne Bestätigung; human = Freigabe, Dashboard-Klick, Direktübergabe
export interface ActionDayCounter {
  /** lesend, nebenwirkungsfrei (Vorprüfung in ApprovalGate.decide) */
  count(
    tx: DbTx | null,
    ctx: DomainContext,
    a: { actionKey: ActionKey; day: string; mode: 'any' | 'auto' },
  ): Promise<number>;
  /**
   * schreibend, idempotent: zählt genau einmal je (ctx.userId, actionKey, executionRef); fordert keine
   * Sperre an (held nur als Nachweis des Sperrprotokolls); LIMIT_REACHED nur bei neuer executionRef über
   * der Grenze – nie für consent.optout.record; reservierte Schlüssel → Wurf.
   * `personIds`: alle betroffenen Personen (`[]` nur ohne Personenbezug), Grundlage des Löschvertrags
   * E-j Nr. 2c; das Präfix `'erased:'` ist reserviert → Wurf.
   */
  consume(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: {
      actionKey: ActionKey;
      executionRef: string;
      mode: ActionExecutionMode;
      personIds: readonly PersonId[];
    },
  ): Promise<DomainResult<{ counted: boolean; day: string }>>;
}
